import { createSlice, createEntityAdapter, PayloadAction, EntityAdapter } from '@reduxjs/toolkit'
import type { ClientModel } from '@itemize/server/models'
import { api } from '../../api.ts'

// Create entity adapter for normalized state management
const clientAdapter = createEntityAdapter({
  selectId: (client: ClientModel) => client.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
})

// Define additional state beyond the normalized entities
export interface ClientSliceState {
  entities: ReturnType<typeof clientAdapter.getInitialState>
  selectedClientId: string | null
  filters: {
    search: string
    status: 'active' | 'inactive' | 'all'
  }
  ui: {
    isCreating: boolean
    editingClientId: string | null
  }
}

const initialState: ClientSliceState = {
  entities: clientAdapter.getInitialState(),
  selectedClientId: null,
  filters: {
    search: '',
    status: 'all',
  },
  ui: {
    isCreating: false,
    editingClientId: null,
  },
}

export const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    // Custom actions for UI state and client-side operations
    selectClient: (state, action: PayloadAction<string | null>) => {
      state.selectedClientId = action.payload
    },
    // Custom reducer for optimistic updates
    updateClientOptimistic: (
      state,
      action: PayloadAction<Partial<ClientModel> & { id: string }>,
    ) => {
      clientAdapter.updateOne(state.entities, {
        id: action.payload.id,
        changes: action.payload,
      })
    },
  },
  extraReducers: (builder) => {
    // Handle RTK Query fulfilled actions to sync with normalized state
    builder
      .addMatcher(
        api.endpoints.getClients.matchFulfilled,
        (state, action: PayloadAction<ClientModel[]>) => {
          // Sync API data with normalized state
          clientAdapter.setAll(state.entities, action.payload)
        },
      )
      .addMatcher(
        api.endpoints.getClient.matchFulfilled,
        (state, action: PayloadAction<ClientModel>) => {
          // Add or update single client
          clientAdapter.upsertOne(state.entities, action.payload)
        },
      )
  },
})

// Export actions
export const { selectClient, updateClientOptimistic } = clientSlice.actions

// Export selectors using entity adapter
export const clientSelectors = clientAdapter.getSelectors(
  (state: { client: ClientSliceState }) => state.client.entities,
)

// Custom selectors
export const selectSelectedClientId = (state: { client: ClientSliceState }) =>
  state.client.selectedClientId

export const selectSelectedClient = (state: { client: ClientSliceState }) => {
  const selectedId = state.client.selectedClientId
  return selectedId ? clientSelectors.selectById(state, selectedId) : null
}

export const selectClientFilters = (state: { client: ClientSliceState }) => state.client.filters

export const selectClientUI = (state: { client: ClientSliceState }) => state.client.ui

export const selectFilteredClients = (state: { client: ClientSliceState }) => {
  const allClients = clientSelectors.selectAll(state)
  const { search, status } = state.client.filters

  return allClients.filter((client) => {
    const matchesSearch = search === '' || client.name.toLowerCase().includes(search.toLowerCase())
    // Note: Assuming isActive property exists on ClientModel - adjust based on your actual model
    const matchesStatus =
      status === 'all' || (status === 'active' && true) || (status === 'inactive' && false)

    return matchesSearch && matchesStatus
  })
}

export default clientSlice.reducer
